import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSettings } from "@/contexts/SettingsContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Search,
  Users,
  Calendar,
  Loader2,
  MoreHorizontal,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";
import usePersistentState from "@/hooks/usePersistentState";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function CohortList() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = usePersistentState("cohorts:filter", "active");
  const [search, setSearch] = usePersistentState("cohorts:search", "");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    cohort_type: "standard",
    start_date: "",
    end_date: "",
    instructor: "",
    description: "",
  });

  const fetchCohorts = useCallback(async () => {
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await axios.get(`${API}/cohorts`, {
        params,
        withCredentials: true,
      });
      setCohorts(data);
    } catch {
      toast.error("Failed to load cohorts");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchCohorts();
  }, [fetchCohorts]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.start_date) payload.start_date = null;
      if (!payload.end_date) payload.end_date = null;
      if (!payload.instructor) payload.instructor = null;
      if (!payload.description) payload.description = null;
      await axios.post(`${API}/cohorts`, payload, { withCredentials: true });
      toast.success("Cohort created successfully");
      setShowCreate(false);
      setForm({ name: "", cohort_type: "standard", start_date: "", end_date: "", instructor: "", description: "" });
      fetchCohorts();
    } catch {
      toast.error("Failed to create cohort");
    } finally {
      setCreating(false);
    }
  };

  const filtered = cohorts.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const filterTabs = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "archived", label: "Archived" },
  ];

  return (
    <div className="space-y-6" data-testid="cohort-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2
            className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight"
            data-testid="cohorts-title"
          >
            Cohort Management
          </h2>
          <p className="text-[#6E7787] font-body text-sm mt-1">
            Manage your {(settings.learner_label || "learner").toLowerCase()} groups
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="create-cohort-btn"
          className="h-10 px-5 font-body font-medium text-white rounded-lg transition-all hover:opacity-90 shrink-0"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Create Cohort
        </Button>
      </div>

      {/* Filters + Search */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex gap-1 bg-[#11141A] border border-white/10 rounded-lg p-1">
          {filterTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              data-testid={`filter-${tab.value}`}
              className={`px-3 py-1.5 rounded-md text-sm font-body transition-all ${
                filter === tab.value
                  ? "text-white bg-white/10"
                  : "text-[#6E7787] hover:text-[#A1A9B8]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cohorts..."
            data-testid="cohort-search"
            className="pl-9 bg-[#11141A] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-9"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2
            className="h-6 w-6 animate-spin"
            style={{ color: "var(--primary-color, #4A90D9)" }}
          />
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center"
          data-testid="cohort-empty-state"
        >
          <div
            className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{
              backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 10%, transparent)",
            }}
          >
            <Users className="h-7 w-7" style={{ color: "var(--primary-color, #4A90D9)" }} />
          </div>
          <h3 className="font-body text-lg font-bold text-white mb-2">
            {search ? "No matching cohorts" : "No cohorts yet"}
          </h3>
          <p className="text-[#6E7787] font-body text-sm max-w-sm mx-auto">
            {search
              ? "Try adjusting your search or filter."
              : "Create your first cohort to get started."}
          </p>
          {!search && (
            <Button
              onClick={() => setShowCreate(true)}
              data-testid="empty-create-cohort-btn"
              className="mt-5 h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Create Cohort
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="cohort-table">
          {/* Table header */}
          <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2 text-xs text-[#6E7787] font-body uppercase tracking-wider">
            <div className="col-span-4">Name</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Start Date</div>
            <div className="col-span-2">{settings.learner_label || "Learner"}s</div>
            <div className="col-span-2">Created</div>
          </div>
          {/* Rows */}
          {filtered.map((cohort) => (
            <div
              key={cohort.id}
              onClick={() => navigate(`/admin/cohorts/${cohort.id}`)}
              data-testid={`cohort-row-${cohort.id}`}
              className="bg-[#11141A] border border-white/10 rounded-lg px-4 py-3.5 cursor-pointer hover:border-white/20 transition-all duration-200 group"
            >
              <div className="sm:grid sm:grid-cols-12 sm:gap-4 sm:items-center">
                <div className="col-span-4 flex items-center gap-3">
                  <div
                    className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 12%, transparent)",
                    }}
                  >
                    <Users className="h-4 w-4" style={{ color: "var(--primary-color, #4A90D9)" }} />
                  </div>
                  <span className="text-sm font-body font-medium text-white truncate" data-testid={`cohort-name-${cohort.id}`}>
                    {cohort.name}
                  </span>
                  <Badge className="bg-white/5 text-[#6E7787] border-white/10 text-[9px] font-body ml-1 shrink-0">{cohort.cohort_type === "micro" ? "Micro" : "Standard"}</Badge>
                </div>
                <div className="col-span-2 mt-2 sm:mt-0">
                  <Badge
                    data-testid={`cohort-status-${cohort.id}`}
                    className={
                      cohort.status === "active"
                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20"
                        : "bg-[#6E7787]/15 text-[#6E7787] border-[#6E7787]/20 hover:bg-[#6E7787]/20"
                    }
                  >
                    {cohort.status}
                  </Badge>
                </div>
                <div className="col-span-2 mt-1 sm:mt-0 flex items-center gap-1.5 text-sm text-[#A1A9B8] font-body">
                  <Calendar className="h-3.5 w-3.5 text-[#6E7787] hidden sm:block" />
                  {formatDate(cohort.start_date)}
                </div>
                <div className="col-span-2 mt-1 sm:mt-0 text-sm text-[#A1A9B8] font-body">
                  {cohort.learner_count ?? 0}
                </div>
                <div className="col-span-2 mt-1 sm:mt-0 flex items-center justify-between">
                  <span className="text-sm text-[#6E7787] font-body">
                    {formatDate(cohort.created_at)}
                  </span>
                  <MoreHorizontal className="h-4 w-4 text-[#6E7787] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Cohort Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-lg" data-testid="create-cohort-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">
              Create Cohort
            </DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              Set up a new group for your {(settings.learner_label || "Learner").toLowerCase()}s.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">
                Cohort Name <span className="text-red-400">*</span>
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Spring 2026 Nursing Cohort"
                required
                data-testid="create-cohort-name"
                className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Cohort Type</Label>
              <div className="flex gap-2" data-testid="create-cohort-type">
                {[{ v: "standard", l: "Standard", desc: "Single enrollment" }, { v: "micro", l: "Micro", desc: "Multiple enrollments" }].map(({ v, l, desc }) => (
                  <button key={v} type="button" onClick={() => setForm({ ...form, cohort_type: v })} data-testid={`cohort-type-${v}`} className={`flex-1 p-3 rounded-lg border text-left transition-colors ${form.cohort_type === v ? "border-[var(--primary-color,#4A90D9)] bg-[var(--primary-color,#4A90D9)]/10" : "border-white/10 bg-[#0A0C10] hover:border-white/20"}`}>
                    <p className={`text-sm font-body font-medium ${form.cohort_type === v ? "text-white" : "text-[#A1A9B8]"}`}>{l}</p>
                    <p className="text-[10px] text-[#6E7787] font-body mt-0.5">{desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">Start Date</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="create-cohort-start-date"
                  className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10 [color-scheme:dark]"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">End Date</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="create-cohort-end-date"
                  className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10 [color-scheme:dark]"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Instructor</Label>
              <Input
                value={form.instructor}
                onChange={(e) => setForm({ ...form, instructor: e.target.value })}
                placeholder="Instructor name"
                data-testid="create-cohort-instructor"
                className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of this cohort"
                rows={3}
                data-testid="create-cohort-description"
                className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] resize-none"
              />
            </div>
            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowCreate(false)}
                data-testid="create-cohort-cancel"
                className="text-[#A1A9B8] hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating || !form.name.trim()}
                data-testid="create-cohort-save"
                className="font-body font-medium text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
              >
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                    Creating...
                  </>
                ) : (
                  "Create Cohort"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
